import React, { FC } from 'react';
import dynamic from 'next/dynamic';
import { NextSeo } from 'next-seo';
import { useIntl } from 'react-intl';
import Slider from 'react-slick';
// import Link from 'next/link';
// import { useRouter } from 'next/router';

import {
  Heading,
  Box,
  Flex,
  Container,
  Stack,
  Text,
  Button,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
  useDisclosure,
} from '@chakra-ui/react';
// import { FiInfo } from 'react-icons/fi';

import ScreenWrapper from '../../src/components/Layouts/ScreenWrapper';
import BaseImage from '../../src/components/Base/BaseImage/BaseImage';
import BaseChakraImage from '../../src/components/Base/BaseChakraImage/BaseChakraImage';
const ModalYachtInsurance = dynamic(
  () => import('../../src/components/Modals/ModalYacht'),
);

import {
  Benefits,
  EasySteps,
  Partners,
  PleasureCrafts,
  Promises,
  Questions,
  GoogleReviews,
} from '../../src/constants/data/yacht';

interface QuestionObjProps {
  id: string;
  question: string;
  answer: string;
  link: string;
}

interface QuestionCompProps {
  questions: Array<QuestionObjProps>;
}

const BASE_URL = process.env.BASE_URL;
const sliderSettings = {
  dots: true,
  arrows: true,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  centerPadding: '0px',
  rows: 1,
  centerMode: true,
  focusOnSelect: true,
  responsive: [
    {
      breakpoint: 600,
      settings: { slidesToShow: 1, slidesToScroll: 1 },
    },
    {
      breakpoint: 900,
      settings: { slidesToShow: 2, slidesToScroll: 1 },
    },
  ],
};

const YachtPage = () => {
  // const router = useRouter();
  const { formatMessage } = useIntl();
  const f: (id: string) => string = (id: string) => formatMessage({ id });
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure({ defaultIsOpen: false });

  const SectionBanner = () => (
    <Box
      p='10'
      bgImage={'url(/assets/img/yacht/banner/background.png)'}
      bgRepeat='no-repeat'
      backgroundSize='cover'
      backgroundPosition='center'
    >
      <Container maxW='5xl' p='0'>
        <Box align='center'>
          <Flex
            justify='center'
            align='center'
            bgColor='white'
            px='5'
            py='2'
            borderRadius='7px'
            maxW='500px'
            wrap='wrap'
            boxShadow='0px 0px 7px white'
            mb='10'
          >
            <Flex w='80px' align='center'>
              <BaseImage
                src={'/assets/img/yacht/banner/google-logo.png'}
                alt='Google'
                dimensions={[80, 25]}
                priority={true}
              />
            </Flex>
            <Text mx='2'>4.9/5 {f('rating')}</Text>
            <Flex w='100px' h='16px' align='center'>
              <BaseImage
                src={'/assets/img/yacht/banner/rating-stars.png'}
                alt='Google Rating'
                dimensions={[100, 16]}
                priority={true}
              />
            </Flex>
            <Text mx='2'>(15,000+ {f('reviews')})</Text>
          </Flex>
          <Flex align='flex-end' wrap={{ base: 'wrap', lg: 'nowrap' }}>
            <Box p='5' w='full'>
              <Box align='center' p='5' bgColor='white'>
                <Text
                  as='h1'
                  lineHeight='1.3em'
                  fontSize={['2xl', '3xl', 40]}
                  fontWeight='700'
                  mb='2'
                >
                  COMPARE{' '}
                  <Text as='span' color='imColor.primary'>
                    YACHT INSURANCE
                  </Text>{' '}
                  WITH ALFRED
                </Text>
                <Text fontSize='md' mb='8'>
                  {f('floatAround')}
                </Text>
                <Box
                  as={'button'}
                  bgColor='imColor.secondary'
                  py='2'
                  px='4'
                  mb='1'
                  borderRadius='2px'
                  border='1px'
                  borderColor='white'
                  transition='0.2s'
                  color='white'
                  _hover={{ bgColor: 'imColor.primary' }}
                  onClick={onOpenModal}
                >
                  <Flex align='center'>
                    <Text
                      fontSize={['20px', '23px', '25px']}
                      fontWeight='700'
                      casing='uppercase'
                    >
                      {f('getQuotes')}
                    </Text>
                    {/* <Icon as={FiChevronRight} boxSize='35px' ms='3' /> */}
                  </Flex>
                </Box>
                <Text fontSize='xs'>{f('weWillEmail')}</Text>
              </Box>
            </Box>
            <Box w='full'>
              <BaseImage
                src={'/assets/img/yacht/banner/alfred-yacht.png'}
                alt='Yacht Insurance'
                objectFit='contain'
                dimensions={[550, 300]}
                priority={true}
              />
            </Box>
          </Flex>
        </Box>
      </Container>
    </Box>
  );

  const SectionPartners = () => (
    <Box px='10'>
      <Container maxW='5xl' p='0'>
        <Box align='center' py='0'>
          <Box px={['2', '5']}>
            <Text
              as='h2'
              fontSize={['2xl', '3xl', '4xl']}
              fontWeight='700'
              mb='2'
            >
              {f('ourYachtInsurance')}
            </Text>
            <Text fontSize='md' color='imColor.black' mb='5'>
              {f('oneSizeDoesNot')}
            </Text>
          </Box>
          <Box pt='8'>
            <SimpleGrid columns={[1, 2, 3]} spacingX='40px' spacingY='30px'>
              {Partners.map(({ title, icon }) => (
                <Box p='2' h='120px' shadow='md' key={title}>
                  <Flex
                    h='100%'
                    display='flex'
                    direction='column'
                    justify='center'
                    align='center'
                  >
                    <BaseImage
                      src={icon}
                      alt={title}
                      objectFit='contain'
                      dimensions={[300, 120]}
                    />
                  </Flex>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      </Container>
    </Box>
  );

  const SectionPleasureCraft = () => (
    <Box px='10'>
      <Container maxW='5xl' p='0'>
        <Box align='center' py='10'>
          <Box px={['2', '5']}>
            <Text
              as='h2'
              fontSize={['2xl', '3xl', '4xl']}
              fontWeight='700'
              mb='2'
            >
              {f('benefitsOfPleasure')}
            </Text>
          </Box>
          <Box h={['100px', '150px', '200px', '280px']} mb='-10'>
            <BaseChakraImage
              src={'/assets/img/yacht/cartoon-semi.png'}
              h='100%'
              alt={f('benefitsOfPleasure')}
            />
          </Box>
          <Box
            p={{ md: '10' }}
            pt='10'
            shadow={{ md: '0px 2px 12px lightgray' }}
          >
            <SimpleGrid columns={[1, 2, 3]} spacingX='40px' spacingY='30px'>
              {PleasureCrafts.map(({ title, text, icon }) => (
                <Flex direction='column' p='3' key={text} shadow='lg'>
                  <Box h='120px'>
                    <BaseImage
                      src={icon}
                      alt={f(title)}
                      dimensions={[100, 120]}
                    />
                  </Box>
                  <Box>
                    <Text fontSize='xl' mb='2'>
                      {f(title)}
                    </Text>
                    <Text noOfLines={5}>{f(text)}</Text>
                  </Box>
                </Flex>
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      </Container>
    </Box>
  );

  const SectionEasySteps = () => (
    <Box px='10'>
      <Container maxW='5xl' p='0'>
        <Box align='center' py='0'>
          <Box px={['2', '5']}>
            <Text
              as='h2'
              fontSize={['2xl', '3xl', '4xl']}
              fontWeight='700'
              mb='2'
            >
              {f('HeadingSectionEasySteps')}
            </Text>
          </Box>
          <Box pt='8'>
            <SimpleGrid columns={[1, 1, 3]} spacingX='50px' spacingY='30px'>
              {EasySteps.map(({ text, icon }, index) => (
                <Flex
                  direction='column'
                  p='5'
                  minH='150px'
                  shadow='xl'
                  borderRadius='3px'
                  color='white'
                  bgColor={
                    index % 2 === 0 ? 'imColor.secondary' : 'imColor.primary'
                  }
                  key={text}
                >
                  <Box h='120px'>
                    <BaseImage
                      src={icon}
                      alt={f(text)}
                      objectFit='contain'
                      dimensions={[80, 90]}
                    />
                  </Box>
                  <Text fontSize='lg' noOfLines={2}>
                    {f(text)}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      </Container>
    </Box>
  );

  const SectionBenefits = () => (
    <Box px='10'>
      <Container maxW='5xl' p='0'>
        <Box align='center' py='10'>
          <Box px={['2', '5']}>
            <Text
              as='h2'
              fontSize={['2xl', '3xl', '4xl']}
              fontWeight='700'
              mb='2'
            >
              {f('HeadingSectionBenefits')}
            </Text>
            <Text fontSize='md' color='imColor.black' mb='5'>
              {f('yachtsAreBigInvestment')}
            </Text>
            <Text fontSize='md' color='imColor.black' fontWeight='700'>
              {f('owningAndBuying')}
            </Text>
          </Box>
          <Box pt='10'>
            <SimpleGrid columns={[1, 2, 3]} spacingX='40px' spacingY='30px'>
              {Benefits.map(({ title, text, icon }) => (
                <Flex direction='column' p='3' key={text} shadow='lg'>
                  <Box h='120px'>
                    <BaseImage
                      src={icon}
                      alt={f(title)}
                      objectFit='contain'
                      dimensions={[145, 120]}
                    />
                  </Box>
                  <Box>
                    <Text fontSize='xl' mb='2'>
                      {f(title)}
                    </Text>
                    <Text noOfLines={5}>{f(text)}</Text>
                  </Box>
                </Flex>
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      </Container>
    </Box>
  );

  const SectionPromises = () => (
    <Box px='10'>
      <Container maxW='5xl' p='0'>
        <Box align='center' py='0'>
          <Box px={['2', '5']}>
            <Text
              as='h2'
              fontSize={['2xl', '3xl', '4xl']}
              fontWeight='700'
              mb='2'
            >
              {f('HeadingSectionPromises')}
            </Text>
          </Box>
          <Box pt='8'>
            <SimpleGrid columns={[2, 2, 4]} spacing={['20px', '40px']}>
              {Promises.map(({ text, icon }) => (
                <Flex
                  direction='column'
                  justify='space-between'
                  p={[2, 3]}
                  border='1px'
                  borderColor='imColor.primary-light'
                  minH='150px'
                  key={text}
                >
                  <Box h='80px'>
                    <BaseImage
                      src={icon}
                      alt={f(text)}
                      objectFit='contain'
                      dimensions={[80, 80]}
                    />
                  </Box>
                  <Text fontSize={['sm', 'md']} noOfLines={4}>
                    {f(text)}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      </Container>
    </Box>
  );

  const SectionReviews = () => (
    <Box px='10'>
      <Container maxW='5xl' p='0'>
        <Box align='center' py='5'>
          <Box px={['2', '5']}>
            <Text
              as='h2'
              fontSize={['2xl', '3xl', '4xl']}
              fontWeight='700'
              mb='2'
            >
              {f('HeadingSectionReviews')}
            </Text>
          </Box>
          <Box pt='8'>
            <Slider {...sliderSettings}>
              {GoogleReviews.map(({ avatar, name, reviewText, id }) => (
                <Flex
                  direction='column'
                  p='3'
                  mb='10'
                  cursor='pointer'
                  key={id}
                >
                  <Box h='110px'>
                    <BaseImage
                      src={avatar}
                      alt={name}
                      objectFit='contain'
                      dimensions={[100, 100]}
                    />
                  </Box>
                  <Box>
                    <Text fontSize='xl' casing='uppercase' fontWeight='600'>
                      {name}
                    </Text>
                    <Box h='25px' w='85px'>
                      <BaseImage
                        src={'/assets/img/yacht/banner/rating-stars.png'}
                        alt='Rating Stars'
                        objectFit='contain'
                        dimensions={[85, 25]}
                      />
                    </Box>
                    <Text noOfLines={10}>{reviewText}</Text>
                  </Box>
                </Flex>
              ))}
            </Slider>
          </Box>
        </Box>
      </Container>
    </Box>
  );

  const SectionQuestions: FC<QuestionCompProps> = ({ questions }) => (
    <Box px='10'>
      <Container maxW='5xl' p='0'>
        <Box align='center' py='10'>
          <Box px={['2', '5']}>
            <Text
              as='h2'
              fontSize={['2xl', '3xl', '4xl']}
              fontWeight='700'
              mb='2'
            >
              {f('HeadingSectionQuestions')}
            </Text>
            <Text fontSize='md' color='imColor.black'>
              {f('readAlfredsAdvice')}
            </Text>
          </Box>
          <Box pt='10'>
            <Accordion allowToggle allowMultiple>
              {questions.map(({ question, answer, link, id }) => (
                <AccordionItem
                  bgColor='imColor.primary-light'
                  color='imColor.black'
                  key={id}
                >
                  {({ isExpanded }) => (
                    <>
                      <Box>
                        <AccordionButton
                          _focus={{ outline: 'none' }}
                          _expanded={{
                            bgColor: 'white',
                            color: 'imColor.primary',
                          }}
                        >
                          <Box flex='1' textAlign='start'>
                            <Text
                              fontSize={['md', 'lg', 'xl']}
                              fontWeight={'700'}
                              p='2'
                            >
                              {f(question)}
                            </Text>
                          </Box>
                          <AccordionIcon color='black' />
                        </AccordionButton>
                      </Box>
                      <AccordionPanel
                        textAlign='start'
                        px='6'
                        bgColor={isExpanded ? 'white' : 'inherit'}
                      >
                        <Text mb='6' noOfLines={4}>
                          {f(answer)}
                        </Text>
                        <Button
                          as={'a'}
                          variant='link'
                          colorScheme='twitter'
                          href={link}
                          target='_blank'
                          rel='noreferrer'
                        >
                          {f('learnMore')}
                        </Button>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              ))}
            </Accordion>
          </Box>
        </Box>
      </Container>
    </Box>
  );

  const SectionGetInsurance = () => (
    <Box px='10'>
      <Container maxW='5xl' bgColor='imColor.primary-light'>
        <Box align='center' px={['2', '5']} py='10'>
          <Text
            as='h2'
            fontSize={['2xl', '3xl', '4xl']}
            fontWeight='700'
            mb='2'
          >
            {f('HeadingSectionGetInsurance')}
          </Text>
          <Heading as='h3' size='md' mb='7'>
            {f('quotesInYourEmailNow')}
          </Heading>
          <Text fontSize='lg' mb='3'>
            {f('thousandsOfCustomers')}
          </Text>
          <Box
            as={'button'}
            px='6'
            py='2'
            mb='1'
            maxW='450px'
            bgColor='imColor.secondary'
            borderRadius='2px'
            border='1px'
            borderColor='white'
            transition='0.2s'
            color='white'
            _hover={{ bgColor: 'imColor.primary' }}
            onClick={onOpenModal}
          >
            <Flex align='center'>
              <Text
                fontSize={['22px', '28px']}
                fontWeight='700'
                casing='uppercase'
              >
                {f('getQuotes')}
              </Text>
              {/* <Icon as={FiChevronRight} boxSize='35px' ms='3' /> */}
            </Flex>
          </Box>
          <Text fontSize='xs'>{f('weWillEmailYourCustom')}</Text>
        </Box>
      </Container>
    </Box>
  );

  return (
    <>
      <NextSeo
        title={`${f('title')}`}
        description={`${f('metaDesc')}`}
        canonical={`${BASE_URL}yacht-insurance/`}
        openGraph={{
          url: `${BASE_URL}yacht-insurance/`,
          title: `${f('title')}`,
          description: `${f('metaDesc')}`,
          images: [
            {
              url: `${BASE_URL}assets/img/yacht/banner/alfred-yacht.png`,
              alt: 'Yacht Insurance',
              width: 481,
              height: 291,
            },
          ],
        }}
      />
      <Box>
        <ScreenWrapper>
          <SectionBanner />
          <Box py='10'>
            <Stack direction='column' spacing='10'>
              <SectionPartners />
              <SectionPleasureCraft />
              <SectionEasySteps />
              <SectionBenefits />
              <SectionPromises />
              <SectionReviews />
              <SectionQuestions questions={Questions} />
              <SectionGetInsurance />
            </Stack>
          </Box>
          <ModalYachtInsurance isOpen={isOpenModal} onClose={onCloseModal} />
        </ScreenWrapper>
      </Box>
    </>
  );
};

export default YachtPage;
